import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronRight, ClipboardList, Phone, Search, X } from "lucide-react";

type Target = { el: HTMLElement; title: string };

/** The page's own section headings: every <h2> in <main>, in order. */
function collectSections(): Target[] {
  return [...document.querySelectorAll<HTMLElement>("main h2")]
    .filter((h) => h.offsetParent !== null && !h.closest("nav, footer, aside, [role=dialog]"))
    .map((h) => ({ el: h, title: (h.textContent ?? "").replace(/\s+/g, " ").trim() }))
    .filter((t) => t.title.length > 0);
}

/**
 * The home page's page helpers, for any page: the BROWSE tab (a panel that
 * jumps to each section — on the right edge on a desktop, a round button
 * bottom-left on a phone) and the floating GET FREE QUOTE button.
 *
 * The sections are read from the page when the panel opens — its <h2>
 * headings — so a page built from data (a country or visa guide) lists
 * exactly the sections it shows. BROWSE is left out on a page with fewer
 * than two, and where `browse` is false: the client wants it on the home page
 * and every page except the index (listing) pages, the Profile pages and the
 * Services pages, which keep only GET FREE QUOTE.
 */
export default function PageJumpBar({ onQuote, browse = true }: { onQuote: () => void; browse?: boolean }) {
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState<Target[]>([]);
  const [hasSections, setHasSections] = useState(false);
  const { pathname } = useLocation();

  // count the sections once the page has drawn them (data-built sections can
  // arrive a moment after the first paint)
  useEffect(() => {
    setHasSections(false);
    const check = () => setHasSections(collectSections().length >= 2);
    const t1 = window.setTimeout(check, 400);
    const t2 = window.setTimeout(check, 2000);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [pathname]);

  const openPanel = () => {
    const found = collectSections();
    setSections(found);
    setHasSections(found.length >= 2);
    if (found.length >= 2) setOpen(true);
  };

  const jump = (el: HTMLElement) => {
    // clear the sticky header (64px on a phone, 80px from md)
    const header = window.innerWidth >= 768 ? 96 : 80;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - header, behavior: "smooth" });
    setOpen(false);
  };

  return (
    <>
      {browse && hasSections && (
        <>
          {/* Desktop: vertical BROWSE tab on the right edge */}
          <div className="hidden md:flex fixed z-[60] right-0 top-1/2 -translate-y-1/2 -translate-x-2 flex-col items-end">
            <button
              type="button"
              onClick={openPanel}
              aria-label="Browse page sections"
              className="bg-[#335765] text-white py-5 px-2.5 rounded-l-2xl shadow-lg border-y-2 border-l-2 border-white/40 hover:border-white/60 transition-colors duration-300 flex flex-col items-center justify-center gap-2 font-black text-base tracking-tighter"
            >
              <span className="drop-shadow-sm">B</span>
              <span className="drop-shadow-sm">R</span>
              <Search size={16} strokeWidth={3.5} className="drop-shadow-sm" />
              <span className="drop-shadow-sm">W</span>
              <span className="drop-shadow-sm">S</span>
              <span className="drop-shadow-sm">E</span>
            </button>
          </div>

          {/* Phone: round BROWSE button, bottom-left */}
          <button
            type="button"
            onClick={openPanel}
            className="md:hidden fixed bottom-6 left-5 z-50 bg-[#335765] text-white rounded-full py-3.5 w-[140px] shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 font-bold border-2 border-white/20 active:scale-95 whitespace-nowrap"
          >
            <Search size={18} className="-ml-1" />
            <span>BROWSE</span>
          </button>
        </>
      )}

      {/* Floating quote button, bottom-right on every screen */}
      <button
        type="button"
        onClick={onQuote}
        className="fixed bottom-6 right-5 md:right-6 z-50 bg-[#9A6040] text-white rounded-full py-3.5 w-[140px] md:w-auto md:px-6 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 font-bold border-2 border-white/20 active:scale-95 whitespace-nowrap"
      >
        <Phone size={20} className="-ml-1 md:ml-0" />
        <span className="hidden md:inline tracking-wide">GET FREE QUOTE</span>
        <span className="md:hidden">QUOTE</span>
      </button>

      {/* The sections panel */}
      <div
        className={`fixed inset-0 z-[70] transition-all duration-500 flex justify-end ${open ? "visible" : "invisible"}`}
        onClick={() => setOpen(false)}
        role="dialog"
        aria-modal="true"
        aria-label="Page sections"
      >
        <div className={`absolute inset-0 bg-black/60 transition-opacity duration-500 ${open ? "opacity-100 backdrop-blur-sm" : "opacity-0"}`} />
        <div
          className={`relative w-full max-w-sm h-full bg-[#FCFBF7] shadow-2xl transition-transform duration-500 ease-out transform ${open ? "translate-x-0" : "translate-x-full"} flex flex-col`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-1.5 w-full bg-gradient-to-r from-primary/20 via-primary to-primary/20" />
          <div className="p-4 bg-[#335765] text-white relative overflow-hidden">
            <div className="flex justify-between items-start mb-3">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-px w-6 bg-white/30" />
                  <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-white/50">Navigation</span>
                </div>
                <p className="text-[25px] font-extrabold leading-tight tracking-tight text-white">Page Sections</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2 bg-white/10 hover:bg-white/30 text-white rounded-full transition-all duration-300 border border-white/10 hover:border-white/50"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex items-center gap-2.5 p-2.5 bg-white/5 rounded-xl border border-white/10">
              <ClipboardList className="h-4 w-4 text-white/50 flex-shrink-0" />
              <p className="text-[11px] md:text-xs text-white/70 leading-relaxed italic">"Jump directly to any section in this page."</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2.5">
            {sections.map((s, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => jump(s.el)}
                className="w-full group relative bg-white hover:bg-[#335765] transition-all duration-300 p-3 rounded-xl border-2 border-primary/20 hover:border-primary flex items-center justify-between shadow-md hover:shadow-xl"
              >
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-primary/5 group-hover:bg-white/10 flex items-center justify-center">
                    <span className="text-xs font-black text-primary group-hover:text-white">{(idx + 1).toString().padStart(2, "0")}</span>
                  </div>
                  <span className="text-sm md:text-base font-bold text-primary group-hover:text-white text-left line-clamp-2">{s.title}</span>
                </div>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-primary group-hover:text-white" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
